import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

const upcomingInput = z.object({
  plan_id: z.string(),
  combination_id: z.string().optional().nullable(),
  food_preference_code: z.string().optional().nullable(),
  delivery_rule_id: z.string().optional().nullable(),
  delivery_area_id: z.string().optional().nullable(),
  start_date: z.string(),
});

export type UpcomingInput = z.infer<typeof upcomingInput>;

export type MenuDish = {
  id: string;
  slug: string;
  name: string;
  description: string;
  image_url: string | null;
  meal_slot: string;
  is_veg: boolean;
  calories_kcal?: number;
  protein_g?: number;
  carbs_g?: number;
  fat_g?: number;
  nutrition_confirmed?: boolean;
};

export type MenuSlot = {
  slot: string;
  source?: string;
  primary: MenuDish | null;
  alternates: MenuDish[];
};

export type MenuDay = {
  date: string;
  slots: Record<string, MenuSlot>;
};

export type UpcomingMenu = {
  plan_id: string;
  combination_id: string;
  food_preference_code: string;
  delivery_rule_id: string;
  delivery_area_id: string;
  start_date: string;
  days: MenuDay[];
  snapshot_hash?: string;
};

// HELPER: Recursively unwrap TanStack Start payload
function unwrap(val: any): any {
  if (val && typeof val === 'object' && 'data' in val && Object.keys(val).length === 1) {
    return unwrap(val.data);
  }
  return val;
}

export const getPublicUpcomingMenu = createServerFn({ method: "POST" })
  .inputValidator((raw: unknown) => upcomingInput.parse(unwrap(raw)))
  .handler(async ({ data }): Promise<UpcomingMenu | { error: string }> => {
    try {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      
      const planSlugToId: Record<string, string> = {
        'weight-management': 'dd92ebce-0d73-40cc-9aa1-0c4da75eb6de',
        'weight_management': 'dd92ebce-0d73-40cc-9aa1-0c4da75eb6de',
        'plan_weight_management': 'dd92ebce-0d73-40cc-9aa1-0c4da75eb6de'
      };
      
      const actualPlanId = planSlugToId[data.plan_id] || data.plan_id;

      // Date window shown by the meal-plan page (UTC calendar dates).
      const WINDOW_DAYS = 60;
      const start = new Date(`${String(data.start_date).slice(0, 10)}T00:00:00Z`);
      if (Number.isNaN(start.getTime())) return { error: "Invalid start date" };
      const iso = (d: Date) => d.toISOString().slice(0, 10);
      const mondayOf = (d: Date) => {
        const m = new Date(d);
        m.setUTCDate(m.getUTCDate() - ((m.getUTCDay() + 6) % 7));
        return m;
      };
      const last = new Date(start);
      last.setUTCDate(last.getUTCDate() + WINDOW_DAYS - 1);

      // Only published, non-archived weekly menus for this plan + preference
      // whose week falls inside the window. Each week uses ONLY its own menu;
      // there is no fallback to another week.
      const { data: cycles, error: cyclesErr } = await supabaseAdmin
        .from("menu_cycles")
        .select(`
          id,
          week_start_date,
          created_at,
          menu_cycle_days (
            id,
            day_of_week,
            menu_cycle_day_dishes (
              dish_id,
              meal_slot,
              is_primary,
              sort_order,
              mp2_dishes:dish_id (id, slug, name, image_url, meal_slot, is_veg, description, calories_kcal, protein_g, carbs_g, fat_g, nutrition_confirmed)
            )
          )
        `)
        .eq("plan_id", actualPlanId)
        .eq("food_preference_code", data.food_preference_code || "veg")
        .eq("is_archived", false)
        .eq("pub_status", "published")
        .gte("week_start_date", iso(mondayOf(start)))
        .lte("week_start_date", iso(last))
        .order("created_at", { ascending: false });

      if (cyclesErr) {
        console.error("[lib/menu] cycle lookup failed", cyclesErr);
        return { error: "Failed to fetch menu" };
      }

      // One menu per week start (newest if duplicates exist for the same week).
      const byWeek = new Map<string, any>();
      for (const c of (cycles ?? []) as any[]) {
        const wk = String(c.week_start_date).slice(0, 10);
        if (!byWeek.has(wk)) byWeek.set(wk, c);
      }

      const days: MenuDay[] = [];
      for (let i = 0; i < WINDOW_DAYS; i++) {
        const date = new Date(start);
        date.setUTCDate(date.getUTCDate() + i);
        const dateStr = iso(date);
        const cycle = byWeek.get(iso(mondayOf(date)));
        if (!cycle) continue; // no published menu for this week → unavailable
        const dbDow = (date.getUTCDay() + 6) % 7; // 0 = Monday … 6 = Sunday

        const cycleDay = ((cycle.menu_cycle_days as any[]) || []).find(
          (d) => Number(d.day_of_week) === dbDow,
        );
        if (!cycleDay) continue;
        const slots: Record<string, any> = {};
        const cycleDishes = ((cycleDay.menu_cycle_day_dishes as any[]) || [])
          .slice()
          .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));

        for (const d of cycleDishes) {
          const dish = d.mp2_dishes as any;
          if (!dish) continue;
          const normalizedDish: MenuDish = {
            id: dish.id || d.dish_id,
            slug: dish.slug || "",
            name: String(dish.name || ""),
            description: dish.description || "",
            image_url: dish.image_url || null,
            meal_slot: (d.meal_slot || dish.meal_slot || "").toLowerCase(),
            is_veg: dish.is_veg,
            calories_kcal: dish.calories_kcal,
            protein_g: dish.protein_g,
            carbs_g: dish.carbs_g,
            fat_g: dish.fat_g,
            nutrition_confirmed: dish.nutrition_confirmed,
          };
          const slotKey = normalizedDish.meal_slot;
          if (!slotKey) continue;
          if (!normalizedDish.name || normalizedDish.name === "(Dish name missing)") continue;
          if (!slots[slotKey]) {
            slots[slotKey] = { slot: slotKey, source: "cycle", primary: null, alternates: [] };
          }
          if (d.is_primary && !slots[slotKey].primary) {
            slots[slotKey].primary = normalizedDish;
          } else {
            slots[slotKey].alternates.push(normalizedDish);
          }
        }

        if (Object.keys(slots).length > 0) days.push({ date: dateStr, slots });
      }

      if (days.length > 0) {
        return {
          plan_id: actualPlanId,
          combination_id: data.combination_id || "",
          food_preference_code: data.food_preference_code || "",
          delivery_rule_id: data.delivery_rule_id || "",
          delivery_area_id: data.delivery_area_id || "",
          start_date: data.start_date,
          days,
        };
      }

      return { error: "No menu available for this configuration" };
    } catch (err: any) {
      console.error("[lib/menu] Critical error:", err);
      return { error: err.message || "Failed to fetch menu" };
    }
  });